import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  fetchBatchPlannedInvestments,
  fetchProjectMeasures,
} from "@/services/api";

export function useBatchData(
  projectId: string | null,
  batchIds: string[],
  batchTitles: Record<string, string>
) {
  const queryClient = useQueryClient();
  const [projectMeasurements, setProjectMeasurements] = useState<{
    planned: any[];
    documented: any[];
  }>({ planned: [], documented: [] });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchMeasurements = async () => {
      if (!projectId) return;

      setIsLoading(true);

      try {
        const plannedMeasurements = [];

        // Check for cached data first
        let hasCachedPlannedData = false;

        for (const batchId of batchIds) {
          const cachedPlannedData = queryClient.getQueryData([
            "batch-planned-investments-single",
            batchId,
          ]);
          if (cachedPlannedData) {
            hasCachedPlannedData = true;
            break;
          }
        }

        // Fetch planned investments if not cached
        if (!hasCachedPlannedData) {
          for (const batchId of batchIds) {
            try {
              const plannedData = await fetchBatchPlannedInvestments(batchId);
              plannedMeasurements.push(
                ...plannedData.map((item: any) => ({
                  ...item,
                  batchId,
                  batchTitle: batchTitles[batchId] || batchId,
                }))
              );

              // Update cache
              queryClient.setQueryData(
                ["batch-planned-investments-single", batchId],
                plannedData
              );
            } catch (error) {
              console.error(
                `Error fetching planned investments for batch ${batchId}:`,
                error
              );
            }
          }
        } else {
          for (const batchId of batchIds) {
            const cachedData = queryClient.getQueryData([
              "batch-planned-investments-single",
              batchId,
            ]);
            if (cachedData) {
              plannedMeasurements.push(
                ...(cachedData as any[]).map((item: any) => ({
                  ...item,
                  batchId,
                  batchTitle: batchTitles[batchId] || batchId,
                }))
              );
            }
          }
        }

        // Measures belong to the project directly (not to a specific
        // batch/vintage), so they're fetched once per project rather than
        // once per batch.
        let documentedMeasurements: any[] = [];
        const cachedMeasuresData = queryClient.getQueryData([
          "project-measures-single",
          projectId,
        ]);
        if (cachedMeasuresData) {
          documentedMeasurements = cachedMeasuresData as any[];
        } else {
          try {
            documentedMeasurements = await fetchProjectMeasures(projectId);
            queryClient.setQueryData(
              ["project-measures-single", projectId],
              documentedMeasurements
            );
          } catch (error) {
            console.error(
              `Error fetching documented measures for project ${projectId}:`,
              error
            );
          }
        }

        setProjectMeasurements({
          planned: plannedMeasurements,
          documented: documentedMeasurements,
        });
      } catch (error) {
        console.error(
          `Error fetching measurements for project ${projectId}:`,
          error
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchMeasurements();
  }, [projectId, batchIds, batchTitles, queryClient]);

  return {
    projectMeasurements,
    isLoading,
  };
}
